
import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionLayout from '@/components/SectionLayout';

const Privacy = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [loadMethod, setLoadMethod] = useState<string>('');

  useEffect(() => {
    console.log('Privacy page mounted, starting Termly integration...');
    
    const loadTermlyPolicy = async () => {
      try {
        // Method 1: Try Termly JavaScript SDK
        console.log('Attempting Termly SDK approach...');
        
        const script = document.createElement('script');
        script.src = 'https://app.termly.io/resource-blocker/2f6eea27-f326-4185-a14a-0c13b5f6cacd';
        script.async = true;
        
        script.onload = () => {
          console.log('Termly SDK script loaded');
          setLoadMethod('SDK');
          setIsLoading(false);
          
          // Check if Termly embed function is available
          if (window.termly?.embed) {
            window.termly.embed();
          }
        };
        
        script.onerror = () => {
          console.log('Termly SDK failed, trying iframe...');
          loadIframe();
        };
        
        document.head.appendChild(script);
        
        // Timeout fallback - if SDK doesn't load within 5 seconds, try iframe
        setTimeout(() => {
          if (isLoading) {
            console.log('SDK timeout, falling back to iframe...');
            loadIframe();
          }
        }, 5000);
        
      } catch (error) {
        console.log('Error in loadTermlyPolicy:', error);
        loadIframe();
      }
    };

    const loadIframe = () => {
      console.log('Loading privacy policy via iframe...');
      const embedElement = document.querySelector('[data-termly-embed="true"]');
      
      if (embedElement) {
        // Clear any existing content
        embedElement.innerHTML = '';
        
        const iframe = document.createElement('iframe');
        iframe.src = 'https://app.termly.io/document/privacy-policy/2f6eea27-f326-4185-a14a-0c13b5f6cacd';
        iframe.style.width = '100%';
        iframe.style.height = '600px';
        iframe.style.border = 'none';
        iframe.style.borderRadius = '8px';
        
        iframe.onload = () => {
          console.log('Iframe loaded successfully');
          setLoadMethod('Iframe');
          setIsLoading(false);
        };
        
        iframe.onerror = () => {
          console.log('Iframe failed to load');
          setHasError(true);
          setIsLoading(false);
        };
        
        embedElement.appendChild(iframe);
      }
    };

    // Start the loading process
    loadTermlyPolicy();
    
    // Cleanup function
    return () => {
      const scripts = document.querySelectorAll('script[src*="termly.io"]');
      scripts.forEach(script => script.remove());
    };
    
  }, []);

  return (
    <>
      <Navbar />
      <SectionLayout 
        paddingY="py-16" 
        mobilePaddingY="py-12"
        backgroundColor="bg-white"
      >
        <div className="pt-16 md:pt-20">
          <h1 className="text-3xl md:text-4xl font-bold text-[#003a70] mb-8 text-center">
            Privacy Policy
          </h1>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 md:p-8">
            {isLoading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003a70] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading privacy policy...</p>
              </div>
            )}
            
            {hasError && (
              <div className="text-center py-8">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    Privacy Policy
                  </h2>
                  <p className="text-gray-600 mb-4">
                    We're having trouble loading our embedded privacy policy. 
                  </p>
                </div>
                
                <div className="bg-[#FEE6D8] border border-[#003a70]/20 rounded-lg p-4 mb-4">
                  <p className="text-[#003a70] font-medium mb-2">
                    View Our Complete Privacy Policy
                  </p>
                  <a 
                    href="https://app.termly.io/document/privacy-policy/2f6eea27-f326-4185-a14a-0c13b5f6cacd" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#003a70] hover:underline font-medium"
                  >
                    Open Privacy Policy in New Tab →
                  </a>
                </div>
                
                <p className="text-gray-500 text-sm">
                  If you continue experiencing issues, please contact our support team.
                </p>
              </div>
            )}
            
            {/* Termly embed div */}
            <div 
              data-termly-embed="true"
              data-id="2f6eea27-f326-4185-a14a-0c13b5f6cacd"
              data-type="iframe"
              className={isLoading || hasError ? 'hidden' : 'block'}
              style={{ minHeight: '400px' }}
            ></div>
            
            {!isLoading && !hasError && loadMethod && (
              <div className="text-center mt-4">
                <p className="text-xs text-gray-500">
                  Loaded via {loadMethod} • Policy ID: 2f6eea27-f326-4185-a14a-0c13b5f6cacd
                </p>
              </div>
            )}
          </div>
        </div>
      </SectionLayout>
      <Footer />
    </>
  );
};

export default Privacy;
